/**
 * Replaces ItemChangeCheck() from app.ts using React Router's useBlocker().
 *
 * Checks the same legacy globals (App.originalReportResult, App.originalPersonResult, etc.)
 * and shows a confirm dialog before allowing navigation away from dirty pages.
 */

import { useBlocker } from 'react-router';
import { useEffect, useRef } from 'react';
import { App } from '@/services/app-compat';
import { AccessAuthorities } from '@/models';
import { hasValue, currentUserHasPermission } from '@/utils/legacy-utils';
import { deepCopyProperties } from '@/utils/arrays';

// Read these dynamically so we always get the latest value
const w = window as any;
function getCaseRpt() { return w.CaseRpt; }
function getIsCaseReportEqual() { return w.isCaseReportEqual; }
function getIsNpsdDataEqual() { return w.isNpsdDataEqual; }
function getUnlockLockedReport() { return w.unlockLockedReport; }
function getSetNPSDItemLocked() { return w.setNPSDItemLocked; }
function getIsCallCardScreenLoaded() { return w.isCallCardScreenLoaded; }
function getCurrentPerson() { return w.currentPerson; }
function getCurrentVehicle() { return w.currentVehicle; }
function getCurrentBusiness() { return w.currentBusiness; }
function getCurrentClientGroup() { return w.currentClientGroup; }
import { RelType } from '@/models/other-entities';
import { ModuleType } from '@/constants/codes';
import { getAuthObject } from '@/utils/auth';

function isCallCardDirty(): boolean {
  if (!hasValue(App.currentCCOriginal) || !hasValue(App.currentCC)) {
    return false;
  }
  const original = deepCopyProperties(App.currentCCOriginal);
  const current = deepCopyProperties(App.currentCC);
  // Exclude frontend-only properties that change without user interaction
  for (const copy of [original, current]) {
    delete copy.citationCount;
    delete copy.attachmentInfoList;
  }
  return JSON.stringify(original) !== JSON.stringify(current);
}

interface DirtyCheck {
  isActive: () => boolean;
  isDirty: () => boolean;
  message: string;
  onLeave: () => void;
  onClean: () => void;
}

export function getDirtyChecks(): DirtyCheck[] {
  return [
    {
      isActive: () => App.originalReportResult != null && getCaseRpt()?.currentMDTRpt != null,
      isDirty: () => !getIsCaseReportEqual()(App.originalReportResult, getCaseRpt().currentMDTRpt),
      message: 'Changes have been made to this report, are you sure you want to leave?',
      onLeave() {
        App.originalReportResult = null;
        const rpt = getCaseRpt()?.currentMDTRpt;
        if (hasValue(rpt) && hasValue(rpt.id))
          getUnlockLockedReport()();
      },
      onClean() {
        App.originalReportResult = null;
        const rpt = getCaseRpt()?.currentMDTRpt;
        if (hasValue(rpt) && hasValue(rpt.id))
          getUnlockLockedReport()();
      },
    },
    {
      isActive: () => App.originalPersonResult != null,
      isDirty: () => !getIsNpsdDataEqual()(App.originalPersonResult, getCurrentPerson()),
      message: 'Changes have been made to this person, are you sure you want to leave?',
      onLeave() {
        App.originalPersonResult = null;
        const p = getCurrentPerson();
        if (p?.id !== null)
          getSetNPSDItemLocked()(p.id.toString(), false, RelType[RelType.person], () => {});
      },
      onClean() {
        App.originalPersonResult = null;
        const p = getCurrentPerson();
        if (p?.id !== null && currentUserHasPermission(AccessAuthorities.ROLE_NPSD_DATA_UPDATE)
            && getAuthObject().company_id === p.createdByCompanyId)
          getSetNPSDItemLocked()(p.id.toString(), false, RelType[RelType.person], () => {});
      },
    },
    {
      isActive: () => App.originalVehicleResult != null,
      isDirty: () => !getIsNpsdDataEqual()(App.originalVehicleResult, getCurrentVehicle()),
      message: 'Changes have been made to this vehicle, are you sure you want to leave?',
      onLeave() {
        App.originalVehicleResult = null;
        const v = getCurrentVehicle();
        if (v?.id !== null)
          getSetNPSDItemLocked()(v.id.toString(), false, RelType[RelType.vehicle], () => {});
      },
      onClean() {
        App.originalVehicleResult = null;
        const v = getCurrentVehicle();
        if (v?.id !== null && currentUserHasPermission(AccessAuthorities.ROLE_NPSD_DATA_UPDATE)
            && getAuthObject().company_id === v.createdByCompanyId)
          getSetNPSDItemLocked()(v.id.toString(), false, RelType[RelType.vehicle], () => {});
      },
    },
    {
      isActive: () => App.originalBusinessResult != null,
      isDirty: () => !getIsNpsdDataEqual()(App.originalBusinessResult, getCurrentBusiness()),
      message: 'Changes have been made to this business, are you sure you want to leave?',
      onLeave() {
        App.originalBusinessResult = null;
        const b = getCurrentBusiness();
        if (b?.id !== null)
          getSetNPSDItemLocked()(b.id.toString(), false, RelType[RelType.business], () => {});
      },
      onClean() {
        App.originalBusinessResult = null;
        const b = getCurrentBusiness();
        if (b?.id !== null && currentUserHasPermission(AccessAuthorities.ROLE_NPSD_DATA_UPDATE)
            && getAuthObject().company_id === b.createdByCompanyId)
          getSetNPSDItemLocked()(b.id.toString(), false, RelType[RelType.business], () => {});
      },
    },
    {
      isActive: () => App.originalCGResult != null,
      isDirty: () => !getIsNpsdDataEqual()(App.originalCGResult, getCurrentClientGroup()),
      message: 'Changes have been made to this client, are you sure you want to leave?',
      onLeave() {
        App.originalCGResult = null;
        const cg = getCurrentClientGroup();
        if (cg?.id !== null)
          getSetNPSDItemLocked()(cg.id.toString(), false, RelType[RelType.client], () => {});
      },
      onClean() {
        App.originalCGResult = null;
        const cg = getCurrentClientGroup();
        if (cg?.id !== null)
          getSetNPSDItemLocked()(cg.id.toString(), false, RelType[RelType.client], () => {});
      },
    },
    {
      isActive: () => {
        if (!(getIsCallCardScreenLoaded()?.() ?? false) || App.callCardController == null) {
          return false;
        }
        const ctrl = App.callCardController;
        if (ctrl.module === ModuleType.mdt) {
          return ctrl.isEditMode === true;
        }
        // CAD: active when the form is editable (open call or editing a closed call)
        return ctrl.callCardNotClosed || ctrl.isEditingClosedCallCard;
      },
      isDirty: () => isCallCardDirty(),
      message: 'Changes have been made to this call card, are you sure you want to leave?',
      onLeave() {},
      onClean() {},
    },
  ];
}

/**
 * Hook that blocks navigation when unsaved changes are detected.
 * Replicates the behavior of ItemChangeCheck() from app.ts.
 */
export function useUnsavedChangesBlocker(): void {
  const pendingBlockerRef = useRef<{ proceed: () => void; reset: () => void } | null>(null);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    // If pauseItemChangeCheck is set, consume it and allow navigation.
    // Must be checked before the same-path check so it is always reset.
    if (App.pauseItemChangeCheck) {
      App.pauseItemChangeCheck = false;
      return false;
    }

    // Don't block if navigating to the same path
    if (currentLocation.pathname === nextLocation.pathname) return false;

    // Check for dirty state
    const checks = getDirtyChecks();
    for (const check of checks) {
      if (check.isActive()) {
        if (check.isDirty()) {
          return true; // Block navigation — we'll handle it in the effect
        } else {
          check.onClean();
          return false;
        }
      }
    }
    return false;
  });

  useEffect(() => {
    if (blocker.state !== 'blocked') return;

    // Find the active dirty check to get the message and cleanup
    const checks = getDirtyChecks();
    let activeCheck: DirtyCheck | null = null;
    for (const check of checks) {
      if (check.isActive() && check.isDirty()) {
        activeCheck = check;
        break;
      }
    }

    if (!activeCheck) {
      blocker.proceed();
      return;
    }

    // Store blocker ref for the confirm dialog
    pendingBlockerRef.current = blocker;

    // Show confirm dialog using OrnaReact.confirm (imperative modal)
    const w = window as any;
    w.OrnaReact.confirm({
      message: activeCheck.message,
      confirmText: 'Leave',
      cancelText: 'Stay',
      confirmClass: 'danger',
    }).then((confirmed: boolean) => {
      if (confirmed) {
        activeCheck!.onLeave();
        blocker.proceed();
      } else {
        blocker.reset();
      }
      pendingBlockerRef.current = null;
    });
  }, [blocker.state]);
}
